// App — Matrix v2 koren
// Task #6 = demo komponentov, Task #7a = auth, Task #7b = PartneriView + routing
//
// Routing (react-router-dom v6):
//   /              → Domov (Demo komponentov)
//   /partneri      → zoznam partnerov
//   /partneri/:id  → detail partnera

import React, { useState, useMemo } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { Layout, useBreadcrumb } from './components/Layout.jsx'
import TrinityFab from './components/TrinityFab.jsx'
import { Btn, BtnAdd, BtnDelete, BtnNav, BtnTotal, BtnGhost } from './components/Btn.jsx'
import { Input, NumberInput, Textarea } from './components/Input.jsx'
import { Modal } from './components/Modal.jsx'
import { Table, THead, TBody, TR, TH, TD } from './components/Table.jsx'
import { useToast } from './components/Toast.jsx'
import { useShortcuts } from './hooks/useShortcuts.js'
import { useDirtyState } from './hooks/useDirtyState.js'
import { COLORS, TYPO, SPACE, RADIUS, SK, CELL, todayLocal } from './theme.js'
import { AuthProvider, useAuth } from './auth/AuthContext.jsx'
import { PinProvider } from './hooks/usePinGate.jsx'
import { ConfirmProvider } from './components/ConfirmDialog.jsx'
import LoginScreen from './auth/LoginScreen.jsx'
import PartneriView from './views/PartneriView.jsx'
import KontaktyView from './views/KontaktyView.jsx'   // p93 — register kontaktných osôb (131.10)
import KontaktyPrehladView from './views/KontaktyPrehladView.jsx'   // p101 — všetky kontakty, skupiny, súhlasy, kampane
import ZakazkyView from './views/ZakazkyView.jsx'
import FakturyView from './views/FakturyView.jsx'
import DosleFaV3 from './views/DosleFaV3.jsx'   // V3 pilot Došlé FA (131.1 bod 4)
import PredajnaView from './views/PredajnaView.jsx'
import RozpisView from './views/RozpisView.jsx'
import StavbaBytyView from './views/StavbaBytyView.jsx'   // STAVBA_LOGIKA_V1: Byty a partie (pre-flight 3488)
import DennikView from './views/DennikView.jsx'
import ZamestnanciView from './views/ZamestnanciView.jsx'
import PluCatalogView from './views/PluCatalogView.jsx'
import CpKatalogView from './views/CpKatalogView.jsx'
import KatalogyView from './views/KatalogyView.jsx'
import MojDen from './views/MojDen.jsx'   // mojden_v1 (28. 9. 2026): hlavná obrazovka „Môj deň“ podľa funkcie (ústava 3.S.1, 49) — nahrádza MatrixHome.jsx
import ManualyPozicii from './views/ManualyPozicii.jsx'   // mojden_v1: register manuálov pozícií
import ZadrzneView from './views/ZadrzneView.jsx'
import PokladnaView from './views/PokladnaView.jsx'
import Mzdy2026View from './views/Mzdy2026View.jsx'
import DochadzkaView from './views/DochadzkaView.jsx'
import KontrolaPracovnikovView from './views/KontrolaPracovnikovView.jsx'   // 13. 9. 2026 týždenná kontrola „kto pre nás pracuje“
import TrinityView from './views/TrinityView.jsx'
import NastaveniaView from './views/NastaveniaView.jsx'
import ObjednavkyView from './views/ObjednavkyView.jsx'
import CenoveView from './views/CenoveView.jsx'
import PartieView from './views/PartieView.jsx'
import PozicovnaView from './views/PozicovnaView.jsx'
import VozidlaView from './views/VozidlaView.jsx'
import VozidlaTerminy from './views/VozidlaTerminy.jsx'
import PozicovnaReporty from './views/PozicovnaReporty.jsx'
import ReklamacieView from './views/ReklamacieView.jsx'
import ObhliadkyView from './views/ObhliadkyView.jsx'
import DopytyView from './views/DopytyView.jsx'
import SmsCentrum from './views/SmsCentrum.jsx'
import KalkulacieView from './views/KalkulacieView.jsx'
import WipView from './views/WipView.jsx'
import SpotrebaView from './views/SpotrebaView.jsx'
import DokumentyArchivView from './views/DokumentyArchivView.jsx'
import PrepojeniaMKsoftView from './views/PrepojeniaMKsoftView.jsx'   // MKSOFT_VAZBY_V1
import MobileApp from './components/MobileApp.jsx'
import QrLandingView from './views/QrLandingView.jsx'

// Loading splash (kym sa overuje token)
function Splash() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: COLORS.bgSoft,
      color: COLORS.muted,
      fontFamily: TYPO.family,
      fontSize: TYPO.sizes.base
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 32, fontWeight: TYPO.weights.extra, color: COLORS.add, letterSpacing: '-0.03em' }}>Matrix</div>
        <div style={{ marginTop: SPACE.sm, color: COLORS.muted }}>Načítavam…</div>
      </div>
    </div>
  )
}

// Authenticated app — vidí ho len prihlasený user; obal Layout + router routes
function AuthedApp() {
  const { user, logout } = useAuth()
  return (
    <Layout user={{ name: user.cele_meno || user.display_name, funkcia: user.funkcia || '', role: user.role, onLogout: logout }}>
      <Routes>
        <Route path="/" element={<MojDen />} />
        <Route path="/manualy-pozicii" element={<ManualyPozicii />} />
        <Route path="/partneri" element={<PartneriView />} />
        <Route path="/partneri/:id" element={<PartneriView />} />
        <Route path="/kontakty" element={<KontaktyPrehladView />} />
        <Route path="/kontakty/osoby" element={<KontaktyView />} />
        <Route path="/zakazky" element={<ZakazkyView />} />
        <Route path="/zakazky/:id" element={<ZakazkyView />} />
        <Route path="/faktury" element={<FakturyView />} />
        <Route path="/faktury/:id" element={<FakturyView />} />
        <Route path="/dosle-v3" element={<DosleFaV3 />} />
        <Route path="/predajna" element={<PredajnaView />} />
        <Route path="/rozpis" element={<RozpisView />} />
        <Route path="/rozpis/stavba/:projectId" element={<StavbaBytyView />} />
        <Route path="/dennik" element={<DennikView />} />
        <Route path="/dennik/:id" element={<DennikView />} />
        <Route path="/zamestnanci" element={<ZamestnanciView />} />
        <Route path="/pozicovna" element={<PozicovnaView />} />
        <Route path="/zadrzne" element={<ZadrzneView />} />
        <Route path="/kalkulacie" element={<KalkulacieView />} />
        <Route path="/pokladna" element={<PokladnaView />} />
        <Route path="/mzdy2026" element={<Mzdy2026View />} />
        <Route path="/dochadzka" element={<DochadzkaView />} />
        <Route path="/kontrola-pracovnikov" element={<KontrolaPracovnikovView />} />
        <Route path="/trinity" element={<TrinityView />} />
        <Route path="/partie" element={<PartieView />} />
        <Route path="/cenove" element={<CenoveView />} />
        <Route path="/objednavky" element={<ObjednavkyView />} />
        <Route path="/nastavenia" element={<NastaveniaView />} />
        <Route path="/katalog" element={<KatalogyView />} />
        <Route path="/katalog/plu" element={<KatalogyView />} />
        <Route path="/katalog/cp" element={<KatalogyView />} />
        <Route path="/katalog/dodavatelia" element={<KatalogyView />} />
        <Route path="/katalog/vlastne" element={<KatalogyView />} />
        <Route path="/katalog/pozicovna" element={<KatalogyView />} />
        <Route path="/katalog/tech-postupy" element={<KatalogyView />} />
        <Route path="/vozidla" element={<VozidlaView />} />
        <Route path="/vozidla/terminy" element={<VozidlaTerminy />} />
        <Route path="/pozicovna/reporty" element={<PozicovnaReporty />} />
        <Route path="/reklamacie" element={<ReklamacieView />} />
        <Route path="/dopyty" element={<DopytyView />} />
        <Route path="/sms" element={<SmsCentrum />} />
        <Route path="/obhliadky" element={<ObhliadkyView />} />
        <Route path="/wip" element={<WipView />} />
        <Route path="/staty" element={<SpotrebaView />} />
        <Route path="/dokumenty" element={<DokumentyArchivView />} />
        <Route path="/prepojenia-mksoft" element={<PrepojeniaMKsoftView />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <TrinityFab />
    </Layout>
  )
}

// Mobilné roly — VŽDY mobil, nikdy desktop (ústava sek. 5 — bezpečnosť)
const MOBILE_ONLY_ROLES = ['foreman', 'partak', 'omietkar', 'poterar', 'maliar']

// Root — switcher: Splash → LoginScreen → MobileApp / AuthedApp
function Root() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <Splash />
  if (!user) return <LoginScreen />
  const qrm = location.pathname.match(/^\/qr\/(.+)$/)
  if (qrm) return <QrLandingView code={decodeURIComponent(qrm[1])} />
  // admin si môže prepnúť pohľad mview <-> dview (override v localStorage)
  // Núdzový prepínač pohľadu pre admina cez URL: ?view=mobile|desktop|auto (escape-hatch)
  if (user.role === 'admin') {
    try {
      const vq = new URLSearchParams(location.search).get('view')
      if (vq === 'mobile' || vq === 'desktop') localStorage.setItem('kamir_v2_view', vq)
      else if (vq === 'auto') localStorage.removeItem('kamir_v2_view')
    } catch (_) {}
  }
  const override = user.role === 'admin' ? localStorage.getItem('kamir_v2_view') : null
  const autoMobile = MOBILE_ONLY_ROLES.includes(user.role) || window.innerWidth < 760
  return (override ? override === 'mobile' : autoMobile) ? <MobileApp /> : <AuthedApp />
}

export default function App() {
  return (
    <BrowserRouter basename="/v2">
      <AuthProvider>
        <PinProvider>
          <ConfirmProvider>
            <Root />
          </ConfirmProvider>
        </PinProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

function Demo() {
  const toast = useToast()
  const { setTrail } = useBreadcrumb()
  const [modalOpen, setModalOpen] = useState(false)
  const [dirtyDialog, setDirtyDialog] = useState(false)

  // Dirty state ukazka (sek. 53.1, 53.5)
  const form = useDirtyState({ meno: '', poznamka: '', suma: null })

  // Email state (samostatne, len ako uncontrolled demo)
  const [email, setEmail] = useState('')

  // Set breadcrumb pri mount
  React.useEffect(() => {
    setTrail([
      { label: 'Matrix v2', onClick: () => setTrail([]) },
      { label: 'Demo komponentov' }
    ])
    return () => setTrail([])
  }, [setTrail])

  // Klavesove skratky pre demo (sek. 54.4-5)
  useShortcuts(useMemo(() => ([
    { keys: 'mod+s', label: 'Uložiť zmeny', scope: 'Demo', handler: () => {
      toast.success('Uložené (demo)')
      form.reset()
    }},
    { keys: 'alt+n', label: 'Nový záznam', scope: 'Demo', handler: () => {
      toast.info('Nový (demo)')
    }},
    { keys: 'alt+t', label: 'Test toast', scope: 'Demo', handler: () => {
      toast.undo('Niečo sa stalo', () => toast.info('Vrátené späť'))
    }}
  ]), [toast, form]))

  // Sample data pre Table
  const [selectedRow, setSelectedRow] = useState(null)
  const rows = [
    { id: '1', meno: 'Pawel Hajnos',  rola: 'Omietkár', mzda: 1850.50 },
    { id: '2', meno: 'Marek',          rola: 'Service',  mzda: 1950.00 },
    { id: '3', meno: 'Andrej',         rola: 'Majster',  mzda: 2200.75 },
    { id: '4', meno: 'Jana Galová',    rola: 'Office',   mzda: 1600.00 }
  ]

  const handleModalClose = () => {
    if (form.isDirty) setDirtyDialog(true)
    else setModalOpen(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.xxl, maxWidth: 1100 }}>

      {/* HEADER */}
      <section>
        <h1 style={{ margin: 0, fontSize: TYPO.sizes.xxl, fontWeight: TYPO.weights.extra }}>
          Task #6 — makro komponenty
        </h1>
        <p style={{ margin: `${SPACE.xs}px 0 0`, color: COLORS.muted, fontSize: TYPO.sizes.base }}>
          Theme · Btn · Input · Modal · Table · Toast · Layout · ShortcutCheatsheet · useShortcuts · useDirtyState · usePositionMemory
        </p>
      </section>

      {/* FARBY POZADIA BUNIEK — NÁVRH */}
      <Section title="Farby pozadia buniek (ústava 21.15 + 61.4 — CELL token)">
        <p style={{ margin: 0, fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
          Pozadie bunky = jej význam. Jemný kontrast — prečítaš na prvý pohľad. Zdroj: <code>CELL</code> v theme.js.
        </p>
        <div style={{ background: CELL.page.bg, padding: SPACE.md, borderRadius: RADIUS.md, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: TYPO.sizes.sm }}>
            <thead>
              <tr style={{ background: CELL.header.bg }}>
                {['OBJEKT / PODLAŽIE','BYTY','STENY m²','SPOLU m²','ZVÝRAZNENÉ'].map(h => (
                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: CELL.header.text,
                    fontSize: TYPO.sizes.xs, borderBottom: `1px solid ${COLORS.line}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { o:'A · 1.NP', z:'6', input:true },
                { o:'A · 2.NP', z:'6', zebra:true },
                { o:'A · 3.NP', z:'6' },
              ].map((r,i) => (
                <tr key={i} style={{ background: r.zebra ? CELL.zebra.bg : CELL.normal.bg }}>
                  <td style={{ padding:'7px 10px' }}>{r.o}</td>
                  <td style={{ padding:'7px 10px' }}>{r.z}</td>
                  <td style={{ padding:'5px 8px' }}>
                    <span style={{ display:'inline-block', minWidth:64, padding:'4px 8px',
                      background: r.input ? CELL.input.bg : CELL.normal.bg,
                      border: `1px solid ${r.input ? CELL.input.border : COLORS.line}`, borderRadius: RADIUS.sm,
                      textAlign:'right' }}>2,75</span>
                  </td>
                  <td style={{ padding:'7px 10px', background: CELL.sum.bg,
                    color: CELL.sum.text, fontWeight: TYPO.weights.bold, textAlign:'right' }}>222,31</td>
                  <td style={{ padding:'7px 10px', background: CELL.final.bg,
                    color: CELL.final.text, fontWeight: TYPO.weights.bold, textAlign:'right' }}>24,84</td>
                </tr>
              ))}
              <tr style={{ background: CELL.header.bg, fontWeight: TYPO.weights.bold }}>
                <td style={{ padding:'7px 10px', color: CELL.header.text }}>OBJEKT A — medzisúčet</td>
                <td style={{ padding:'7px 10px' }}>18</td>
                <td style={{ padding:'7px 10px' }} />
                <td style={{ padding:'7px 10px', background: CELL.sum.bg,
                  color: CELL.sum.text, textAlign:'right' }}>666,93</td>
                <td style={{ padding:'7px 10px' }} />
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ display:'flex', gap: SPACE.lg, flexWrap:'wrap', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          {[
            [CELL.input.bg,'vstupné pole (krémová + oranž. border)'],
            [CELL.sum.bg,'súčet / SPOLU (zelená)'],
            [CELL.final.bg,'finálny / zvýraznený (amber)'],
            [CELL.header.bg,'hlavička / medzisúčet (modrá)'],
            [CELL.zebra.bg,'zebra (párny riadok)'],
          ].map(([c,l]) => (
            <span key={l} style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ width:18, height:18, background:c, border:`1px solid ${COLORS.line}`, borderRadius:3 }} />
              {l}
            </span>
          ))}
        </div>
      </Section>

      {/* TLACIDLA */}
      <Section title="Tlačidlá (ústava 21.15)">
        <Row>
          <BtnAdd>+ Pridať byt</BtnAdd>
          <BtnDelete>− Zmazať</BtnDelete>
          <BtnNav>Otvoriť detail</BtnNav>
          <BtnTotal>Σ Výmera zákazky</BtnTotal>
          <BtnGhost>Zrušiť</BtnGhost>
        </Row>
        <Row>
          <Btn variant="add" size="sm">Pridať (sm)</Btn>
          <Btn variant="add" size="md">Pridať (md)</Btn>
          <Btn variant="add" size="lg">Pridať (lg)</Btn>
          <Btn variant="add" disabled>Disabled</Btn>
          <Btn variant="add" loading>Loading</Btn>
        </Row>
      </Section>

      {/* INPUTY */}
      <Section title="Inputy — SK čiarka v NumPad (ústava 54.3)">
        <Row>
          <Input
            label="Meno zákazníka"
            value={form.value.meno}
            onChange={(v) => form.setValue({ ...form.value, meno: v })}
            placeholder="napr. Novák"
            hint="Test dirty state — ako začneš písať, * sa objaví v karte modalu"
          />
          <NumberInput
            label="Suma"
            value={form.value.suma}
            onChange={(n) => form.setValue({ ...form.value, suma: n })}
            decimals={2}
            hint="Skús zadat 1234,56 cez NumPad (čiarka sa NEPREVÁDZA na bodku)"
          />
          <Input
            label="Email"
            value={email}
            onChange={setEmail}
            placeholder="firma@example.sk"
            type="email"
            hint={email && !email.includes('@') ? 'Email musí obsahovať @' : 'Nepovinné'}
            error={email && !email.includes('@') ? 'Neplatný email' : ''}
          />
        </Row>
        <Textarea
          label="Poznámka"
          value={form.value.poznamka}
          onChange={(v) => form.setValue({ ...form.value, poznamka: v })}
          placeholder="Lubovoľný text…"
          rows={3}
        />
        {form.isDirty && (
          <div style={{
            padding: SPACE.sm,
            background: COLORS.addBg,
            border: `1px solid ${COLORS.add}`,
            borderRadius: RADIUS.md,
            fontSize: TYPO.sizes.sm,
            color: COLORS.text
          }}>
            ● Dirty state aktívny — pozri tab v prehliadači (* prefix)
            <Btn variant="ghost" size="sm" onClick={() => form.reset()} style={{ marginLeft: SPACE.sm }}>Reset</Btn>
          </div>
        )}
      </Section>

      {/* TABULKA */}
      <Section title="Tabuľka — šípky ↑ ↓ navigujú medzi riadkami (ústava 54.2)">
        <Table
          selectedRow={selectedRow}
          onRowSelect={setSelectedRow}
          onRowOpen={(rk) => toast.info(`Otvoriť detail #${rk}`)}
        >
          <THead>
            <TR>
              <TH>ID</TH>
              <TH>Meno</TH>
              <TH>Rola</TH>
              <TH align="right">Mzda (€)</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map(r => (
              <TR
                key={r.id}
                rowKey={r.id}
                onClick={() => setSelectedRow(r.id)}
                style={selectedRow === r.id ? { background: COLORS.navBg } : undefined}
              >
                <TD mono>{r.id}</TD>
                <TD>{r.meno}</TD>
                <TD>{r.rola}</TD>
                <TD align="right" mono>{SK.eur(r.mzda)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        <p style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, margin: `${SPACE.sm}px 0 0` }}>
          Klikni na tabuľku alebo riadok, potom skús ↑ ↓ Home End PageUp/Down Enter.
        </p>
      </Section>

      {/* TOAST */}
      <Section title="Toast notifikácie (ústava 53.4 Undo)">
        <Row>
          <Btn variant="nav" onClick={() => toast.info('Informačná správa')}>toast.info</Btn>
          <Btn variant="add" onClick={() => toast.success('Hotovo')}>toast.success</Btn>
          <Btn variant="ghost" onClick={() => toast.warn('Pozor — niečo nesedí')}>toast.warn</Btn>
          <Btn variant="delete" onClick={() => toast.error('Chyba: niečo zlyhalo')}>toast.error</Btn>
          <Btn variant="total" onClick={() => toast.undo('Zmena uložená', () => toast.info('Vrátené'))}>toast.undo (10s)</Btn>
        </Row>
      </Section>

      {/* MODAL */}
      <Section title="Modal s Dirty Confirm (ústava 53.1)">
        <Row>
          <Btn variant="nav" onClick={() => setModalOpen(true)}>Otvoriť modal</Btn>
        </Row>
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Test modal"
          subtitle="Otvor inputy, niečo napíš, potom skús zatvoriť — zobrazí sa Dirty dialog"
          isDirty={form.isDirty}
          onDirtyClose={() => setDirtyDialog(true)}
          footer={
            <>
              <BtnGhost onClick={handleModalClose}>Zavrieť</BtnGhost>
              <BtnAdd onClick={() => {
                toast.success('Uložené')
                form.reset()
                setModalOpen(false)
              }}>Uložiť</BtnAdd>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>Obsah modalu — môžeš editovať polia hore na demo stránke a pri zavreti uvidíš Dirty dialog.</p>
          <p>Stlač <kbd style={{ background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, padding: '0 6px', borderRadius: 3 }}>Esc</kbd> — to isté.</p>
        </Modal>

        <Modal.DirtyConfirm
          open={dirtyDialog}
          onSave={() => {
            toast.success('Uložené')
            form.reset()
            setDirtyDialog(false)
            setModalOpen(false)
          }}
          onContinue={() => setDirtyDialog(false)}
          onDiscard={() => {
            form.discard()
            setDirtyDialog(false)
            setModalOpen(false)
          }}
        />
      </Section>

      {/* SKRATKY HINT */}
      <Section title="Klávesové skratky (ústava 54)">
        <p style={{ fontSize: TYPO.sizes.base, color: COLORS.text, lineHeight: TYPO.line.relaxed, margin: 0 }}>
          Vyskúšaj: <kbd style={{ background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, padding: '0 6px', borderRadius: 3 }}>?</kbd> otvorí prehľad všetkých skratiek ·{' '}
          <kbd style={{ background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, padding: '0 6px', borderRadius: 3 }}>Ctrl+S</kbd> (alebo ⌘+S) uloží ·{' '}
          <kbd style={{ background: COLORS.bgSoft, border: `1px solid ${COLORS.line}`, padding: '0 6px', borderRadius: 3 }}>Alt+T</kbd> test undo toast.
        </p>
      </Section>

      {/* BACKEND PING */}
      <Section title="Backend health check">
        <BackendPing />
      </Section>

      <footer style={{
        marginTop: SPACE.xl,
        paddingTop: SPACE.lg,
        borderTop: `1px solid ${COLORS.line}`,
        color: COLORS.muted,
        fontSize: TYPO.sizes.xs
      }}>
        Matrix v2 · Task #6 demo · {todayLocal()} · KAMIR s.r.o. ·
        starý Matrix beží paralelne na <a href="http://localhost:8090" style={{ color: COLORS.nav }}>:8090</a>
      </footer>
    </div>
  )
}

// Layout helpers
function Section({ title, children }) {
  return (
    <section style={{
      background: COLORS.bg,
      border: `1px solid ${COLORS.line}`,
      borderRadius: RADIUS.lg,
      padding: SPACE.xl
    }}>
      <h2 style={{
        margin: `0 0 ${SPACE.md}px`,
        fontSize: TYPO.sizes.lg,
        fontWeight: TYPO.weights.bold,
        color: COLORS.nav
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
        {children}
      </div>
    </section>
  )
}

function Row({ children }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE.md, alignItems: 'flex-end' }}>
      {children}
    </div>
  )
}

function BackendPing() {
  const [status, setStatus] = useState('idle')
  const [data, setData] = useState(null)
  const toast = useToast()

  const ping = async () => {
    setStatus('loading')
    try {
      const r = await fetch('/api/health')
      const j = await r.json().catch(() => ({}))
      setStatus(r.ok ? 'ok' : 'fail')
      setData({ httpStatus: r.status, body: j })
      if (r.ok) toast.success('Backend OK')
      else toast.error(`Backend nedostupný (${r.status})`)
    } catch (e) {
      setStatus('error')
      setData({ error: String(e) })
      toast.error('Spojenie zlyhalo')
    }
  }

  return (
    <div>
      <Row>
        <Btn variant="add" onClick={ping}>Otestovať /api/health</Btn>
        {status !== 'idle' && (
          <span style={{
            padding: '4px 10px',
            background: status === 'ok' ? COLORS.addBg : COLORS.removeBg,
            color: status === 'ok' ? COLORS.add : COLORS.remove,
            borderRadius: RADIUS.sm,
            fontSize: TYPO.sizes.sm,
            fontWeight: TYPO.weights.semibold
          }}>{status}</span>
        )}
      </Row>
      {data && (
        <pre style={{
          marginTop: SPACE.md,
          padding: SPACE.md,
          background: COLORS.bgSoft,
          border: `1px solid ${COLORS.line}`,
          borderRadius: RADIUS.md,
          fontSize: TYPO.sizes.xs,
          overflow: 'auto',
          fontFamily: TYPO.mono
        }}>{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  )
}
